import {
  getHtmlElement,
} from '../../utils/browserUtils';
import { DEFAULT_SELECTOR, primaryClassMap, SecondaryIframe } from './windowClasses';
import { clickToContinueContainer, resetClickToContinue } from '../../utils/clickToContinueUtils';
import { setupMessageForwarding } from './windowListeners';
import errorMap from '../../utils/errorUtils';
import store from '../../utils/store';

// eslint-disable-next-line import/prefer-default-export
class WindowManager {
  constructor({ elementOrSelector = DEFAULT_SELECTOR } = {}) {
    if (WindowManager._instance !== undefined) {
      return WindowManager._instance;
    }
    WindowManager._instance = this;
    this.referenceElement = getHtmlElement(elementOrSelector);
    this._window = {
      /**
       * @type {PrimaryNewTab | PrimaryIframe | undefined}
       */
      primary: undefined,
      /**
       * @type {SecondaryIframe | undefined}
       */
      secondary: undefined,
    };

    setupMessageForwarding(WindowManager);
  }

  static async openPrimaryNewTabByUserClick({ broker, intent }) {
    // rest global "click to continue" promise
    resetClickToContinue();

    // redirect to click to continue page
    ENV !== 'production' && console.log('Redirecting to click-to-continue page');

    const windowManager = new WindowManager();
    windowManager.open({
      secondaryStatus: `click-to-continue/?broker=${broker}`,
      changeOnly: 'secondary',
      broker,
      intent,
    });

    // wait for user click promise to resolve
    await clickToContinueContainer.current.promise;
  }

  async open({
    primaryWindowType = 'newtab', // or "iframe"
    primaryURL,
    secondaryStatus = undefined,
    broker = undefined,
    changeOnly = undefined, // "primary" or "secondary" or undefined (which changes both)
    bypassClickToContinue = false,
    intent = 'default',
    intentData = {},
    recentBrokers = [],
    isReturningUser = false,
    silentIframe = false,
  } = {}) {
    // store variables in global store
    store.intent = intent;
    store.intentData = intentData;
    store.recentBrokers = recentBrokers;
    store.isReturningUser = isReturningUser;

    // Prepare expected window config
    const expectedWindow = {
      primary: {
        windowType: primaryWindowType,
        url: primaryURL,
      },
      secondary: {
        windowType: 'iframe',
        status: secondaryStatus,
      },
    };

    if (expectedWindow.primary.windowType === 'iframe') {
      expectedWindow.secondary = undefined;
    }
    if (
      (primaryURL === undefined) ||
      (primaryWindowType === undefined)
    ) {
      expectedWindow.primary = undefined;
    }
    if (secondaryStatus === undefined) {
      expectedWindow.secondary = undefined;
    }

    // Primary iframe and secondary screen (also iframe) can't be hidden
    // without hiding the other one.
    // So, hide them only if both are expected to be hidden.
    // If any of them is going to remain open,
    // don't hide/show them separately as that will depend on the order of that call.
    const shouldHideIframe = (
      expectedWindow.primary === undefined &&
      expectedWindow.secondary === undefined
    );

    // Cleanup existing window config
    this.cleanupClosedWindows();

    // eslint-disable-next-line no-lone-blocks
    {
      // Compare this._window (current) and expectedWindow (expected)

      // First, compare primary
      if (changeOnly === undefined || changeOnly === 'primary') {
        const isCurrentPrimaryDefined = (this._window.primary !== undefined);
        const isExpectedPrimaryDefined = (expectedWindow.primary !== undefined);
        if (
          isCurrentPrimaryDefined &&
          isExpectedPrimaryDefined &&
          (this._window.primary.windowType === expectedWindow.primary.windowType)
        ) {
          // If current and expected window are present and are of same type,
          // reuse it
          this._window.primary.changeURL(expectedWindow.primary.url);
        } else {
          // Current window is different from expected and can't be reused

          // So, close current window - if it is present
          if (isCurrentPrimaryDefined) {
            this._window.primary.close(shouldHideIframe);
          }

          if (isExpectedPrimaryDefined) {
            if (
              !bypassClickToContinue &&
              expectedWindow.primary.windowType === 'newtab'
            ) {
              WindowManager
                .openPrimaryNewTabByUserClick({ broker, intent })
                // on getting click, cancel this function execution
                // and return the same after a fresh function execution
                .then(() => this.open.call(this, ...arguments));
              return;
            }

            // And then create new window
            const ClassToUse = primaryClassMap[expectedWindow.primary.windowType];

            try {
              // try creating new window/iframe
              this._window.primary = new ClassToUse(expectedWindow.primary.url, silentIframe);
            } catch (err) {
              if (
                (err !== undefined) &&
                (err.message === errorMap.FAILED_TO_OPEN_WINDOW.message)
              ) {
                // if it fails to open window, irrespective of bypassClickToContinue -
                // open through click-to-continue redirection
                await WindowManager.openPrimaryNewTabByUserClick({ broker, intent });

                // on getting click, cancel this function execution
                // and return the same after a fresh function execution
                return this.open.call(this, ...arguments);
              } else {
                throw err;
              }
            }
          }
        }
      }

      // Then, compare secondary
      if (changeOnly === undefined || changeOnly === 'secondary') {
        const isCurrentSecondaryDefined = (this._window.secondary !== undefined);
        const isExpectedSecondaryDefined = (expectedWindow.secondary !== undefined);

        // Note: both this._window.secondary and expectedWindow.secondary can be undefined.
        // windowType can be only 'iframe', so it doesn't matter

        if (
          isCurrentSecondaryDefined &&
          isExpectedSecondaryDefined
        ) {
          // If current and expected windows are not undefined, reuse
          this._window.secondary.changeURL(expectedWindow.secondary.status);
        } else {
          // Either current or expected is undefined, remove and create as needed

          // Close current window - if expected is undefined and current is defined
          if (
            !isExpectedSecondaryDefined &&
            isCurrentSecondaryDefined
          ) {
            this._window.secondary.close(shouldHideIframe);
            this._window.secondary = undefined;
          }

          // Create window - if current is undefined and expected is defined
          if (
            !isCurrentSecondaryDefined &&
            isExpectedSecondaryDefined
          ) {
            this._window.secondary =
              new SecondaryIframe(expectedWindow.secondary.status);
          }
        }
      }
    }

    return undefined;
  }

  cleanupClosedWindows() {
    if (
      (this._window.primary !== undefined) &&
      (!this._window.primary.isOpen())
    ) {
      // clear out any active timers
      if (this._window.primary.cancelWindowClosePromise) {
        this._window.primary.cancelWindowClosePromise();
      }
      this._window.primary = undefined;
    }
    // No need to check for secondary one, as it can only be a iframe
  }

  closeAll() {
    this.cleanupClosedWindows();

    if (this._window.primary !== undefined) {
      this._window.primary.close();
      this._window.primary = undefined;
    }
    if (this._window.secondary !== undefined) {
      // No-op
      this._window.secondary.close();
      this._window.secondary = undefined;
    }
  }

  message(payload) {
    if (this._window.primary) { this._window.primary.message(payload); }
  }
}

export default WindowManager;



// WEBPACK FOOTER //
// ./logic/WindowManager/windowManagerRaw.js